import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  EulogyData,
  eulogyService,
} from '../../../../services/eulogyInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('EulogyData');

type RouteParams = {
  eulogyId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type EulogyDataScreenRouteProp = RouteProp<RootStackParamList, 'EulogyData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const EulogyDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<EulogyDataScreenRouteProp>();
  const [eulogyInfo, setEulogyInfo] = useState<EulogyData | null>(null);
  const [loading, setLoading] = useState(true);

  const eulogyId = route.params?.eulogyId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  useEffect(() => {
    loadEulogyInfo();
  }, []);

  const loadEulogyInfo = async () => {
    try {
      if (eulogyId) {
        const info = await eulogyService.getEulogyById(eulogyId, targetUserId);
        setEulogyInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await eulogyService.getAllEulogy(targetUserId);
        if (allInfo.length > 0) {
          setEulogyInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading eulogy info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (eulogyInfo) {
      // Map the data from eulogyInfo to the format expected by EulogyInputScreen
      const mappedData = {
        id: eulogyInfo.id,
        eulogyPreference: eulogyInfo.eulogyPreference,
        preferredSpeaker: eulogyInfo.preferredSpeaker,
        speakerContact: eulogyInfo.speakerContact,
        keyThemes: eulogyInfo.keyThemes,
        importantStories: eulogyInfo.importantStories,
        accomplishments: eulogyInfo.accomplishments,
        personalQualities: eulogyInfo.personalQualities,
        relationships: eulogyInfo.relationships,
        avoidTopics: eulogyInfo.avoidTopics,
        tonePreference: eulogyInfo.tonePreference,
        lengthPreference: eulogyInfo.lengthPreference,
        specialRequests: eulogyInfo.specialRequests,
        notes: eulogyInfo.notes,
        writerName: eulogyInfo.writerName,
        writerRelationship: eulogyInfo.writerRelationship,
        contactPhone: eulogyInfo.contactPhone,
        contactEmail: eulogyInfo.contactEmail,
        backupWriter: eulogyInfo.backupWriter,
        backupContact: eulogyInfo.backupContact,
        writingStyle: eulogyInfo.writingStyle,
        collaborationPreference: eulogyInfo.collaborationPreference,
        deadlinePreference: eulogyInfo.deadlinePreference,
        sourceMaterials: eulogyInfo.sourceMaterials,
        specialInstructions: eulogyInfo.specialInstructions,
        writerNotes: eulogyInfo.writerNotes,
      };

      navigation.navigate('EulogyInput', {
        eulogyData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('EulogyInput');
    }
  };

  const handleDelete = () => {
    if (eulogyInfo) {
      Alert.alert(
        'Delete Eulogy',
        'Are you sure you want to delete this eulogy? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await eulogyService.deleteEulogy(eulogyInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if needed
                const remainingEntries = await eulogyService.getAllEulogy();
                if (remainingEntries.length === 0) {
                  await tasksService.resetTask('eulogy');
                }

                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting eulogy info:', error);
                Alert.alert('Error', 'Failed to delete eulogy');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Eulogy Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Eulogy Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {eulogyInfo ? (
            <>
              {eulogyInfo.eulogyPreference && (
                <InfoItem label="Eulogy Preference" value={eulogyInfo.eulogyPreference} />
              )}
              {eulogyInfo.preferredSpeaker && (
                <InfoItem label="Preferred Speaker" value={eulogyInfo.preferredSpeaker} />
              )}
              {eulogyInfo.speakerContact && (
                <InfoItem label="Speaker Contact" value={eulogyInfo.speakerContact} />
              )}
              {eulogyInfo.keyThemes && (
                <InfoItem label="Key Themes" value={eulogyInfo.keyThemes} />
              )}
              {eulogyInfo.importantStories && (
                <InfoItem label="Important Stories" value={eulogyInfo.importantStories} />
              )}
              {eulogyInfo.accomplishments && (
                <InfoItem label="Accomplishments" value={eulogyInfo.accomplishments} />
              )}
              {eulogyInfo.personalQualities && (
                <InfoItem label="Personal Qualities" value={eulogyInfo.personalQualities} />
              )}
              {eulogyInfo.relationships && (
                <InfoItem label="Relationships" value={eulogyInfo.relationships} />
              )}
              {eulogyInfo.avoidTopics && (
                <InfoItem label="Topics to Avoid" value={eulogyInfo.avoidTopics} />
              )}
              {eulogyInfo.tonePreference && (
                <InfoItem label="Tone Preference" value={eulogyInfo.tonePreference} />
              )}
              {eulogyInfo.lengthPreference && (
                <InfoItem label="Length Preference" value={eulogyInfo.lengthPreference} />
              )}
              {eulogyInfo.specialRequests && (
                <InfoItem label="Special Requests" value={eulogyInfo.specialRequests} />
              )}
              {eulogyInfo.notes && (
                <InfoItem label="Notes" value={eulogyInfo.notes} />
              )}
              {eulogyInfo.writerName && (
                <InfoItem label="Writer Name" value={eulogyInfo.writerName} />
              )}
              {eulogyInfo.writerRelationship && (
                <InfoItem label="Writer Relationship" value={eulogyInfo.writerRelationship} />
              )}
              {eulogyInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={eulogyInfo.contactPhone} />
              )}
              {eulogyInfo.contactEmail && (
                <InfoItem label="Contact Email" value={eulogyInfo.contactEmail} />
              )}
              {eulogyInfo.backupWriter && (
                <InfoItem label="Backup Writer" value={eulogyInfo.backupWriter} />
              )}
              {eulogyInfo.backupContact && (
                <InfoItem label="Backup Contact" value={eulogyInfo.backupContact} />
              )}
              {eulogyInfo.writingStyle && (
                <InfoItem label="Writing Style" value={eulogyInfo.writingStyle} />
              )}
              {eulogyInfo.collaborationPreference && (
                <InfoItem label="Collaboration Preference" value={eulogyInfo.collaborationPreference} />
              )}
              {eulogyInfo.deadlinePreference && (
                <InfoItem label="Deadline Preference" value={eulogyInfo.deadlinePreference} />
              )}
              {eulogyInfo.sourceMaterials && (
                <InfoItem label="Source Materials" value={eulogyInfo.sourceMaterials} />
              )}
              {eulogyInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={eulogyInfo.specialInstructions} />
              )}
              {eulogyInfo.writerNotes && (
                <InfoItem label="Writer Notes" value={eulogyInfo.writerNotes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No eulogy found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Eulogy</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default EulogyDataScreen;
